import { Box, Button, CircularProgress } from '@mui/material';
import { GridColDef, GridPreProcessEditCellProps, GridRenderCellParams, GridRowModel, useGridApiRef } from '@mui/x-data-grid';
import { AxiosResponse } from 'axios';
import DataGrid from 'components/data-grid';
import DataGridAutocomplete, { Option } from 'components/data-grid/data-grid-autocomplete';
import DataGridSummary, { DataGridSummaryProps, SummaryItem } from 'components/data-grid/data-grid-summary';
import DataGridToolbar from 'components/data-grid/data-grid-toolbar';
import { renderDataGridOption } from 'components/data-grid/render-data-grid-option';
import LoadingSpinner from 'components/loading-spinner';
import { useAccountState } from 'contexts/account/account-context';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import dayjs, { Dayjs } from 'dayjs';
import { useGet } from 'hooks/use-get';
import { usePost } from 'hooks/use-post';

import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { isValidDecimal } from 'utils/validate-numbers';
import { getFeedersBrands, getFeeding, getFeedingPonds, getFeeds, putFeeding } from 'views/production-systems/api/productionSystems';
import Active from 'views/production-systems/assets/Active';
import Inactive from 'views/production-systems/assets/Inactive';
import LockPondContainer from 'views/production-systems/components/lock-pond';
import PondLockTimer from 'views/production-systems/components/pond-lock-timer';
import { usePondsState } from 'views/production-systems/context/ponds-context';
import { UpdateFeedingInput } from 'views/production-systems/features/registration-log/types/update-feeding-input.type';
import { useDataGridError } from 'views/production-systems/hooks/use-data-grid-error';
import { calculateBiomass, calculateMeanWeight, kgToLbs, lbsToKg } from 'views/production-systems/utils/farm-overview';
import RegistrationHeader from '../../../components/registration-header';
import Switch from 'components/switch';

type Props = {
  logId: number;
  eventNr: number;
  handleClose?: () => void;
  canEdit: boolean;
  dateCreated: Dayjs;
};

type Row = {
  pondId: string;
  pondName: string;
  zoneName: string;
  cycleId: number;
  cycleName: string;
  previousQty: number;
  previousMw: number;
  previousBm: number;
  amount: number;
  feed: { id: string; label: string };
  feeder: { id: string; label: string };
};

const PAGE_NAME_ID = 'productionSystems.farmOverview.feeding.edit';
const COSTAMARKET_CUSTOMER_NAME = 'COSTAMARKET';


const EditFeeding: React.FC<Props> = ({ handleClose, logId, eventNr, canEdit = true, dateCreated }) => {
  const intl = useIntl();
  const apiRef = useGridApiRef();
  const queryClient = useQueryClient();
  const { account } = useAccountState();
  const snackbarDispatch = useSnackbarDispatch();
  const { globalSiteId, lockPonds, releasePonds, lockedPonds } = usePondsState();
  const { errorDictionary, changeErrorDictionary, isEditing, hasError, handleEditing } = useDataGridError();

  const [confirm, setConfirm] = useState<boolean>(false);
  const [readOnly, setReadOnly] = useState<boolean>(!canEdit);
  const [enableKG, setEnableKg] = useState<boolean>(true);

  //#region FETCH DATA

  const { data: feedData } = useGet(['getFeeds', account.company?.companyId], ({ queryKey }) => getFeeds(String(queryKey[1])));

  const { data: feederData } = useGet(['getFeeders', account.company?.companyId], ({ queryKey }) => getFeedersBrands(String(queryKey[1])));

  const feeds = useMemo<Option[]>(
    () => [...(feedData ?? [])].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase())).map((f) => ({ id: String(f.id), label: f.name })),
    [feedData]
  );

  const feeders = useMemo<Option[]>(
    () => [...(feederData ?? [])].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase())).map((f) => ({ id: String(f.id), label: f.name })),
    [feederData]
  );

  const { data: feeding, isLoading } = useGet(
    ['getFeeding', globalSiteId, logId, eventNr],
    ({ queryKey }) => getFeeding(String(queryKey[1]), Number(queryKey[2]), Number(queryKey[3]))
  );

  useEffect(() => {
    if (feeding && canEdit) lockPonds(getFeedingPonds(feeding));
  }, [canEdit, feeding, lockPonds]);

  // #endregion

  //#region Data Grid

  const [rows, setRows] = useState<readonly Row[]>([]);

  const processRowUpdate = (newRow: GridRowModel<Row>, oldRow?: GridRowModel<Row>) => {
    // If oldRow is not provided, fallback to finding it from rows
    const prevRow = oldRow || rows.find((row) => row.pondId === newRow.pondId);
    let updatedAmount = newRow.amount;
    // Only convert amount if the amount field was changed
    if (prevRow && newRow.amount !== prevRow.amount) {
      updatedAmount = enableKG ? newRow.amount : lbsToKg(newRow.amount);
    }
    const updatedRow: Row = { ...newRow, amount: updatedAmount };
    setRows(rows.map((row) => (row.pondId === newRow.pondId ? updatedRow : row)));
    return updatedRow;
  };

  const columns: GridColDef[] = useMemo(() => {
    return [
      {
        field: 'pondName',
        flex: 2,
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.destinationPond` }),
        editable: false,
        renderCell: ({ row }: GridRenderCellParams<Row>) => {
          return (
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'center',
                width: '100%',
              }}>
              {!!row.cycleId ? <Active titleAccess="Active" /> : <Inactive titleAccess="Inactive" />}
              <Box
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                title={row.pondName}>
                {row.pondName}
              </Box>
            </Box>
          );
        },
      },
      {
        field: 'cycleName',
        flex: 1,
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.cycleName` }),
        editable: false,
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'zoneName',
        flex: 1,
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.zone` }),
        editable: false,
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'previousQty',
        flex: 1,
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.number` }),
        type: 'number',
        editable: false,
        align: 'right',
        headerAlign: 'right',
      },
      {
        field: 'previousMw',
        flex: 1,
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.meanWeight` }),
        editable: false,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
      },
      {
        field: 'previousBm',
        flex: 1,
        headerName: intl.formatMessage(
          { id: `${PAGE_NAME_ID}.biomass` },
          {
            type: enableKG ? 'kg' : 'lbs'
          }
        ),
        editable: false,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        valueGetter: (value, row) => calculateBiomass(row.previousMw, row.previousQty, enableKG),
      } as GridColDef,
      {
        field: 'feed',
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.feed` }),
        flex: 1,
        type: 'singleSelect',
        valueOptions: feeds,
        valueFormatter: (value: Option) => value?.label,
        renderCell: renderDataGridOption,
        preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
          const hasError = !params.props.value;
          return { ...params.props, error: hasError };
        },
        renderEditCell: (params) => <DataGridAutocomplete params={params} options={feeds} />,
        sortable: false,
        editable: !readOnly,
      } as GridColDef<any, { id: number; label: string }, string>,
      {
        field: 'feeder',
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.feederBrand` }),
        flex: 1,
        type: 'singleSelect',
        valueOptions: feeders,
        valueFormatter: (value: Option) => value?.label,
        renderCell: renderDataGridOption,
        preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
          const hasError = !params.props.value;
          return { ...params.props, error: hasError };
        },
        renderEditCell: (params) => <DataGridAutocomplete params={params} options={feeders} />,
        sortable: false,
        editable: !readOnly,
      } as GridColDef<any, { id: number; label: string }, string>,
      {
        field: 'amount',
        flex: 1,
        headerName: intl.formatMessage(
          { id: `${PAGE_NAME_ID}.feedQuantity` },
          {
            type: enableKG ? 'kg' : 'lbs',
          }
        ),
        editable: !readOnly,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
          const hasError = !isValidDecimal(params.props.value);
          changeErrorDictionary({ ...errorDictionary, [`${params.id}-amount`]: hasError });
          return { ...params.props, error: hasError };
        },
        valueGetter: (value: number) => {
          const amount = enableKG ? value : kgToLbs(value);
          return amount;
        },
      },
    ];
  }, [feeds, feeders, readOnly, enableKG]);

  const summaryValues: SummaryItem[] = useMemo(() => {
    let summary = rows.reduce(
      (prev, curr) => {
        return {
          previousQty: prev.previousQty + curr.previousQty,
          previousBmKg: Number(prev.previousBmKg) + calculateBiomass(curr.previousMw, curr.previousQty, true),
          previousBm: prev.previousBm + curr.previousBm,
          previousMw: 0,
          amount: prev.amount + curr.amount,
        };
      },
      {
        amount: 0,
        previousQty: 0,
        previousBmKg: 0,
        previousMw: 0,
        previousBm: 0,
      }
    );

    summary.previousMw = calculateMeanWeight(summary.previousBmKg, summary.previousQty);
    return [
      {
        value: undefined, // Empty ListItem with 'hide' class
        type: undefined,
        hidden: true,
      },
      {
        value: undefined, // Empty ListItem with 'hide' class
        type: undefined,
        hidden: true,
      },
      {
        value: summary.previousQty,
        type: 'number',
        hidden: false,
      },
      {
        value: summary.previousMw, // The value remains the number itself
        type: 'number',
        hidden: false,
        minimumFractionDigits: 3, // Derived from .toFixed(3)
      },
      {
        value: enableKG ? summary.previousBm : kgToLbs(summary.previousBm), // The value remains the number itself
        type: 'number',
        hidden: false,
        minimumFractionDigits: 3, // Derived from .toFixed(3)
      },
      {
        value: undefined, // Empty ListItem with 'hide' class
        type: undefined,
        hidden: true,
      },
      {
        value: undefined, // Empty ListItem with 'hide' class
        type: undefined,
        hidden: true,
      },
      {
        value: enableKG ? summary.amount : kgToLbs(summary.amount),
        type: 'string', // Assuming amount is a general string or number
        hidden: false,
      },
    ];
  }, [rows, enableKG]);

  //#endregion

  //#region  POST DATA

  const updateFeeding = usePost<AxiosResponse, UpdateFeedingInput>(
    (request) => putFeeding(globalSiteId, logId, request),
    () => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });

      feeding && releasePonds(feeding.cycles.map((cycle) => cycle.pondId));
      queryClient.invalidateQueries({
        queryKey: ['getRegistrations']
      });
      handleClose && handleClose();
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  //#endregion POST DATA

  //#region HANDLERS

  const handleSubmit = () => {
    let data = rows.map((row) => ({
      cycleId: row.cycleId,
      amount: row.amount,
      pondId: row.pondId,
      feedId: Number(row.feed!.id),
      feederId: Number(row.feeder!.id),
    }));

    updateFeeding.mutate({
      cycles: data,
    });
  };

  //#endregion

  //#region HOOKS

  useEffect(() => {
    if (canEdit) {
      if (lockedPonds.notGranted.length > 0 && confirm) {
        setReadOnly(true);
      } else {
        setReadOnly(false);
      }
    }
  }, [confirm, canEdit, lockedPonds.notGranted]);


  /// Determine if KG should be enabled based on customer name
  /// this was requeted in this PBI: https://dev.azure.com/Cargill-CargillGlobal/BluViu/_workitems/edit/1981360
  /// A better solution should e discussed to handle this from the admin site for each customer
  /// Right now will be hardcoded since it was requested as a quick fix by Bussiness
  useEffect(() => {
    if (account.company?.customerName === COSTAMARKET_CUSTOMER_NAME) {
      setEnableKg(false);
    }
  }, [account.company?.customerName]);

  //#endregion HOOKS

  //#region HOOKS

  // add check to disable submit if no feed and feeder
  const disableSubmit = useMemo(() => {
    return rows.some((row) => {
      return !isValidDecimal(row.amount) || Object.values(row.feed).every((x) => !x) || Object.values(row.feeder).every((x) => !x);
    });
  }, [rows]);

  useEffect(() => {
    if (!!feeding) {
      let rows: readonly any[] = feeding.cycles.map((cycle) => {
        let feed = feeds?.find((t) => Number(t.id) === cycle.feedId);
        let feeder = feeders?.find((t) => Number(t.id) === cycle.feederId);

        return {
          pondId: cycle.pondId,
          pondName: cycle.pondName,
          zoneName: cycle.zoneName,
          cycleId: cycle.cycleId,
          cycleName: cycle.cycleName,
          previousQty: cycle.previousQty,
          previousMw: cycle.previousMw,
          previousBm: calculateBiomass(cycle.previousMw, cycle.previousQty, enableKG),
          amount: cycle.amount,
          feed: { id: feed?.id, label: feed?.label },
          feeder: { id: feeder?.id, label: feeder?.label },
        };
      });

      setRows(rows);
    }
  }, [feeding, feeders, feeds, enableKG]);
  //#endregion HOOKS

  return isLoading ? (
    <LoadingSpinner />
  ) : lockedPonds?.notGranted?.length > 0 && !confirm ? (
    <LockPondContainer handleClose={handleClose} setConfirm={setConfirm} fromAdd={false} />
  ) : (
    <Box display={'flex'} flexDirection={'column'} gap={4}>
      {!readOnly && <PondLockTimer handleDialogClose={handleClose} />}
      <Box display={'flex'} flexDirection={'column'} gap={8}>
        <RegistrationHeader dateCreated={dateCreated} />
        <DataGrid
          rows={rows}
          apiRef={apiRef}
          columns={columns}
          getRowId={(row) => row.pondId}
          processRowUpdate={processRowUpdate}
          showToolbar
          slots={{ toolbar: DataGridToolbar, footer: DataGridSummary }}
          slotProps={{
            toolbar: {
              children: (
                <Box display={'flex'} flexDirection={'row'} justifyContent={'space-between'} gap={4} alignItems={'center'}>
                  <Box display={'flex'} flexDirection={'column'} height={40}>
                    <Switch
                      dark
                      value={enableKG}
                      onChange={() => {
                        setEnableKg((prev) => !prev);
                      }}
                      label={intl.formatMessage({ id: `${PAGE_NAME_ID}.switchLabel` })}
                    />
                  </Box>
                </Box>
              ),
              csvOptions: {
                fileName: `${intl.formatMessage({
                  id: 'productionSystems.registrationLog.registrationTypes.feeding',
                })}-${dayjs().format('DD/MM/YYYY')}`,
              },
            },
            footer: {
              summaryValues: summaryValues,
            } as DataGridSummaryProps,
          }}
          onCellEditStop={({ field }) => {
            if (['feed', 'feeder'].includes(field)) return;
            handleEditing();
          }}
          onRowClick={({ id }) => {
            const { field } = apiRef.current?.state.tabIndex.cell ?? {};
            if (['feed', 'feeder'].includes(field ?? '')) return;
            apiRef.current?.getCellParams(id, field as string).isEditable && handleEditing();
          }}
        />
      </Box>
      {!readOnly && (
        <Box display={'flex'} justifyContent={'flex-end'}>
          <Button
            disabled={updateFeeding.isPending || disableSubmit || hasError || isEditing}
            onClick={handleSubmit}
            startIcon={updateFeeding.isPending ? <CircularProgress color="inherit" size={18} /> : null}>
            <FormattedMessage id={'general.confirm'} />
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default React.memo(EditFeeding);
